/**
 * @fileoverview Shared `GridSetupPanel` right-side slide-over drawer for configuring the
 * 12-column dashboard grid (`columns`, `rowHeight`, `gap`, `cardRadius`, `showHeaderBanner`).
 * @module ui-admin/components/DashboardCanvasBuilder/GridSetupPanel
 *
 * Responsibilities: - Render a collapsible right-side drawer (mirrors `PageSetupPanel`) toggled by
 *                     the header "Grid Setup" button in `DashboardBuilderPage` and `SystemDashboardBuilderPage`.
 * Dependencies:     - React, lucide-react
 *                   - @firesquirrel/local-db (DashboardGridSetup)
 *                   - ./dashboardDefaults (DEFAULT_DASHBOARD_LAYOUT)
 * Architecture:     - Presentation-only controlled component (`value` + `onChange`).
 * Notes:            - Closes gap G-22.
 */

import React from 'react';
import { X, Grid3X3 } from 'lucide-react';
import type { DashboardGridSetup } from '@firesquirrel/local-db';
import { DEFAULT_DASHBOARD_LAYOUT } from './dashboardDefaults';

/**
 * @contract GridSetupPanelProps
 * Purpose: Props for `GridSetupPanel`.
 * Fields:  open, onClose, value, onChange, disabled?
 * Rules:   Controlled component; emits updated `DashboardGridSetup`.
 * Sync:    N/A.
 */
export interface GridSetupPanelProps {
  open: boolean;
  onClose: () => void;
  value?: DashboardGridSetup;
  onChange: (next: DashboardGridSetup) => void;
  disabled?: boolean;
}

const ROW_HEIGHT_OPTIONS = [
  { value: 60, label: 'Compact (60px)' },
  { value: 80, label: 'Standard (80px)' },
  { value: 100, label: 'Comfortable (100px)' },
  { value: 120, label: 'Wall Display (120px)' },
];

const GAP_OPTIONS = [
  { value: 8, label: 'Tight (8px)' },
  { value: 16, label: 'Standard (16px)' },
  { value: 24, label: 'Spacious (24px)' },
];

const RADIUS_OPTIONS: Array<{ value: NonNullable<DashboardGridSetup['cardRadius']>; label: string }> = [
  { value: 'none', label: 'Square (0px)' },
  { value: 'sm', label: 'Small (4px)' },
  { value: 'md', label: 'Medium (8px)' },
  { value: 'lg', label: 'Large (12px)' },
];

/**
 * @component GridSetupPanel
 * Purpose:     Configure dashboard grid columns, row height, card gap, border radius, and header banner.
 * Props:       GridSetupPanelProps.
 * Behavior:    Renders when `open` is true; calls `onChange` on any control change.
 * Data Source: none (controlled).
 * Constraints: presentation-only; no persistence.
 * Performance: stateless.
 */
export function GridSetupPanel({
  open,
  onClose,
  value,
  onChange,
  disabled = false,
}: GridSetupPanelProps) {
  if (!open) return null;

  const current: DashboardGridSetup = {
    ...DEFAULT_DASHBOARD_LAYOUT,
    ...value,
  };

  const update = (patch: Partial<DashboardGridSetup>) => {
    onChange({ ...current, ...patch });
  };

  return (
    <div
      data-testid="grid-setup-panel"
      role="dialog"
      aria-label="Grid Setup"
      className="w-72 shrink-0 bg-white border-l border-gray-200 flex flex-col h-full shadow-lg z-20"
    >
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Grid3X3 className="h-4 w-4 text-orange-600" />
          <h3 className="text-sm font-semibold text-gray-900">Grid Setup</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close Grid Setup"
          className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="p-4 space-y-4 overflow-y-auto flex-1 text-xs">
        <div>
          <label className="block font-medium text-gray-700 mb-1">Grid Columns</label>
          <select
            disabled={disabled}
            aria-label="Grid Columns"
            value={current.columns}
            onChange={(e) => update({ columns: Number(e.target.value) })}
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
          >
            <option value={12}>12 Columns (Standard)</option>
            <option value={8}>8 Columns</option>
            <option value={6}>6 Columns</option>
          </select>
        </div>

        <div>
          <label className="block font-medium text-gray-700 mb-1">Row Height</label>
          <select
            disabled={disabled}
            aria-label="Row Height"
            value={current.rowHeight}
            onChange={(e) => update({ rowHeight: Number(e.target.value) })}
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
          >
            {ROW_HEIGHT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-medium text-gray-700 mb-1">Grid Gap</label>
          <select
            disabled={disabled}
            aria-label="Grid Gap"
            value={current.gap ?? 16}
            onChange={(e) => update({ gap: Number(e.target.value) })}
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
          >
            {GAP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block font-medium text-gray-700 mb-1">Card Corner Radius</label>
          <select
            disabled={disabled}
            aria-label="Card Corner Radius"
            value={current.cardRadius ?? 'md'}
            onChange={(e) =>
              update({ cardRadius: e.target.value as DashboardGridSetup['cardRadius'] })
            }
            className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md bg-white"
          >
            {RADIUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center justify-between pt-2 border-t border-gray-100 cursor-pointer">
          <span className="font-medium text-gray-700">Show Header Banner</span>
          <input
            type="checkbox"
            disabled={disabled}
            checked={current.showHeaderBanner ?? true}
            onChange={(e) => update({ showHeaderBanner: e.target.checked })}
            className="rounded border-gray-300 text-orange-600"
          />
        </label>
      </div>
    </div>
  );
}

export default GridSetupPanel;
